// Composer Studio — 스크롤 힌트 영역.
//
// 스크롤바를 숨기되(좌우 패널 등에서 시각 소음 제거), 스크롤 가능한 방향에 상/하단 chevron(▲/▼)을
// 오버레이로 표시해 "더 있음"을 알린다(admin 사이드바 Layout.tsx와 동일 패턴 — 라이트 테마).
// 스크롤 컨테이너는 h-full로 부모(셸 패널 본문 등)를 채운다. className은 내부 스크롤 div에 부가된다.
//
// 측정: scrollTop>1(위 더 있음) / scrollTop+clientHeight<scrollHeight-1(아래 더 있음).
//       onScroll + ResizeObserver(컨테이너+직속 자식 크기 변화) + window resize로 갱신.

import { useCallback, useEffect, useRef, useState } from 'react';

function ChevronHint({ className }: { className?: string }): JSX.Element {
  return (
    <svg
      className={`h-4 w-4 text-gray-400 ${className ?? ''}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      aria-hidden
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

export function ScrollHintArea({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const [scroll, setScroll] = useState<{ up: boolean; down: boolean }>({ up: false, down: false });

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const up = el.scrollTop > 1;
    const down = el.scrollTop + el.clientHeight < el.scrollHeight - 1;
    setScroll((prev) => (prev.up === up && prev.down === down ? prev : { up, down }));
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    update();
    // 컨테이너 + 직속 자식(내용 늘고 줄 때) 크기 변화 감지.
    const ro = new ResizeObserver(update);
    const observeChildren = () => {
      ro.observe(el);
      for (const child of Array.from(el.children)) ro.observe(child);
    };
    observeChildren();
    // 직속 자식 집합/내용 변동(추가·교체·서브트리 변경) 시 새 자식 재관찰 + 재측정
    // (ResizeObserver는 mount 시점 자식만 보므로, 자식 수가 바뀌면 stale해질 수 있어 보완).
    const mo = new MutationObserver(() => {
      observeChildren();
      update();
    });
    mo.observe(el, { childList: true, subtree: true });
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      mo.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [update]);

  return (
    <div className="relative flex-1 min-h-0">
      {scroll.up && (
        <div className="pointer-events-none absolute inset-x-0 top-0 z-10 flex justify-center bg-gradient-to-b from-white via-white/80 to-transparent pb-2 pt-0.5">
          <ChevronHint className="rotate-180" />
        </div>
      )}
      <div
        ref={ref}
        onScroll={update}
        className={`h-full overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden ${className ?? ''}`}
      >
        {children}
      </div>
      {scroll.down && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 flex justify-center bg-gradient-to-t from-white via-white/80 to-transparent pb-0.5 pt-2">
          <ChevronHint />
        </div>
      )}
    </div>
  );
}
